import i18n from '@/utils/i18n';
import {
  BaseInboundMetadataFields,
  InboundListItem,
  OrderMetadata,
  SearchInput,
} from '@/screens/order-inbounds/types';
import { endReducer } from '@/utils/end-reducer';
import {
  getMinAndMaxQuantity,
  removeAdComponents,
} from '@/contexts/item-context-utils';
import {
  getTermFromInput,
  getTitleFromInput,
} from '@/screens/order-inbounds/utils';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import {
  ItemsContext_VariantsPackFieldsFragment,
  SmartOrderSearchInputsFieldsFragment,
} from '@/types';
import { ListOption, ListOptionValue } from '@/components/list-item';
import { SmartOrderSearchTermStatus } from '@/enums';
import {
  useAdaptAndGroupVariantPacks,
  VariantPacksAdapter,
} from './use-adapt-and-group-item-list-options';
import { useReducer } from 'react';
import {
  useVariantPackInfoContext,
  VariantPackInfoType,
} from '@/contexts/variant-pack-info-context';

export type InboundReducerItemMetadata = BaseInboundMetadataFields & {
  variantPack: ItemsContext_VariantsPackFieldsFragment | null;
};

export interface VariantPackWithQuantities {
  quantity: number;
  variantPackUUID: string;
}

export type InputToQuantityMap = Record<string, VariantPackWithQuantities>;

type InboundReducerItem = ListOption<InboundReducerItemMetadata>;

export interface MatchTerm {
  searchInputUUID: string;
  term: string;
}

export interface OrderInboundState {
  inputToQuantityMap: InputToQuantityMap;
  listItems: InboundReducerItem[];
  matchTerms: MatchTerm[];
  quantitiesAdjusted: boolean;
}

export const initialOrderInboundState: OrderInboundState = {
  inputToQuantityMap: {},
  listItems: [],
  matchTerms: [],
  quantitiesAdjusted: false,
};

export type InboundReducerAction =
  | {
      payload: {
        orderMetadata: OrderMetadata[];
      };
      type: 'HYDRATE';
    }
  | {
      payload: {
        itemToAdd: ItemListOptionMetadata;
        quantity: number;
        searchInput?: SmartOrderSearchInputsFieldsFragment;
        searchInputUUID: string;
      };
      type: 'ADD_ITEM';
    }
  | {
      payload: {
        clientSearchInputUUID: string;
        searchInput: SmartOrderSearchInputsFieldsFragment;
      };
      type: 'CONFIRM_ADD_ITEM';
    }
  | {
      payload: {
        itemMetadata: ItemListOptionMetadata;
        previousItem: InboundListItem;
        searchInput?: SmartOrderSearchInputsFieldsFragment;
      };
      type: 'SWAP_ITEM';
    }
  | {
      payload: {
        itemUUID: ListOptionValue;
        status: SmartOrderSearchTermStatus;
      };
      type: 'SET_ITEM_STATUS';
    }
  | {
      payload: {
        quantity: number;
        searchInputUUID: string;
      };
      type: 'UPDATE_QUANTITY';
    }
  | {
      payload: {
        searchInputUUIDs: string[];
      };
      type: 'REMOVE_ITEMS';
    }
  | {
      payload: {
        itemNotes: string | null;
        searchInputUUID: string;
      };
      type: 'UPDATE_ITEM_NOTES';
    };

type VariantPack =
  SmartOrderSearchInputsFieldsFragment['search_term']['variants_pack'];

interface GetInboundListItemArgs {
  input?: SearchInput;
  listOption?: ItemListOption | null;
  matchTerm: string;
  searchInputUUID: string;
  searchTerm: string;
  title: string;
  value: string;
  variantPack?: ItemsContext_VariantsPackFieldsFragment | null;
}

const buildSearchInput = (
  searchInput: SmartOrderSearchInputsFieldsFragment,
): SearchInput => ({
  itemNotes: searchInput.item_notes ?? null,
  matchedText: searchInput.matched_text || null,
  packUnit: searchInput.pack_unit || null,
  quantity: searchInput.quantity,
  result: searchInput,
  searchTerm: searchInput.search_term.term,
  status: searchInput.search_term.status as SmartOrderSearchTermStatus,
  uuid: searchInput.uuid,
});

const getInboundListItem = ({
  input,
  listOption,
  matchTerm,
  searchInputUUID,
  searchTerm,
  title,
  value,
  variantPack,
}: GetInboundListItemArgs): InboundReducerItem => {
  const inputWithResult: SearchInput = input ?? {
    itemNotes: null,
    matchedText: null,
    packUnit: null,
    quantity: 1,
    result: null,
    searchTerm,
    status: SmartOrderSearchTermStatus.DRAFT,
    uuid: searchInputUUID,
  };

  return {
    metadata: {
      inputWithResult,
      listOption: listOption || null,
      matchTerm,
      variantPack: variantPack || null,
    },
    title,
    value,
  };
};

export const generateInitialInboundState = (
  orderMetadata: OrderMetadata[],
  vpAdapter: VariantPacksAdapter,
  getVariantPackInfo: (variantPackUUID: string) => VariantPackInfoType | null,
): OrderInboundState => {
  const initialInputToQuantityMap: InputToQuantityMap = {};

  const matchTerms: MatchTerm[] = [];
  let quantitiesAdjusted = false;
  const listItems = orderMetadata.flatMap((order) =>
    order.searchInputs.reduce<InboundReducerItem[]>((acc, input) => {
      // skip items with quantity 0
      if (input.quantity === 0) {
        return acc;
      }
      const searchTerm = input.searchTerm;
      const result = input.result;
      let variantPack: VariantPack = undefined;
      const matchTerm = getTermFromInput(input, i18n.locale);
      matchTerms.push({
        searchInputUUID: input.uuid,
        term: matchTerm,
      });
      let initialQuantity = input.quantity;

      if (result && result.search_term.variants_pack) {
        variantPack = result.search_term.variants_pack;
        const { maximumQuantity, minimumQuantity } = getMinAndMaxQuantity(
          getVariantPackInfo(variantPack.uuid),
        );
        if (
          typeof minimumQuantity === 'number' &&
          initialQuantity < minimumQuantity
        ) {
          initialQuantity = minimumQuantity;
          quantitiesAdjusted = true;
        }
        if (
          typeof maximumQuantity === 'number' &&
          initialQuantity > maximumQuantity
        ) {
          initialQuantity = maximumQuantity;
          quantitiesAdjusted = true;
        }

        if (variantPack) {
          initialInputToQuantityMap[input.uuid] = {
            quantity: initialQuantity,
            variantPackUUID: variantPack!.uuid,
          };
        }
      }

      acc.push(
        getInboundListItem({
          input,
          listOption: variantPack ? vpAdapter([variantPack])[0] : null,
          matchTerm,
          searchInputUUID: input.uuid,
          searchTerm,
          title: getTitleFromInput(input),
          value: input.uuid,
          variantPack: variantPack || null,
        }),
      );
      return acc;
    }, []),
  );
  return {
    inputToQuantityMap: initialInputToQuantityMap,
    listItems,
    matchTerms,
    quantitiesAdjusted,
  };
};

const orderInboundReducer = (
  vpAdapter: VariantPacksAdapter,
  getVariantPackInfo: (variantPackUUID: string) => VariantPackInfoType | null,
) => {
  return (
    state: OrderInboundState,
    action: InboundReducerAction,
  ): OrderInboundState => {
    switch (action.type) {
      case 'HYDRATE': {
        const { orderMetadata } = action.payload;
        return generateInitialInboundState(
          orderMetadata,
          vpAdapter,
          getVariantPackInfo,
        );
      }
      case 'ADD_ITEM': {
        const { itemToAdd, searchInput, quantity, searchInputUUID } =
          action.payload;
        const nextItemToAdd = removeAdComponents(itemToAdd);
        const matchTerm = nextItemToAdd.displayName;
        let newSearchInput: SearchInput | undefined;

        if (searchInput) {
          newSearchInput = buildSearchInput(searchInput);
        }

        const nextVariantPack = newSearchInput?.result
          ? newSearchInput.result.search_term.variants_pack
          : null;

        const nextSearchInputUUID = searchInputUUID;
        const nextInputToQuantityMap = {
          ...state.inputToQuantityMap,
          [nextSearchInputUUID]: {
            quantity,
            variantPackUUID: itemToAdd.variantPackUUID,
          },
        };

        const nextListItems: InboundReducerItem[] = [
          ...state.listItems,
          getInboundListItem({
            input: newSearchInput,
            listOption: {
              metadata: {
                ...nextItemToAdd,
                skus: [nextItemToAdd],
              },
              title: matchTerm,
              value: nextItemToAdd.variantPackUUID,
            },
            matchTerm,
            searchInputUUID: nextSearchInputUUID,
            searchTerm: nextItemToAdd.displayName,
            title: `"${nextItemToAdd.displayName}"`,
            value: nextSearchInputUUID,
            variantPack: nextVariantPack,
          }),
        ];

        return {
          ...state,
          inputToQuantityMap: nextInputToQuantityMap,
          listItems: nextListItems,
          matchTerms: [
            ...state.matchTerms,
            {
              searchInputUUID: nextSearchInputUUID,
              term: matchTerm,
            },
          ],
        };
      }
      case 'CONFIRM_ADD_ITEM': {
        const { clientSearchInputUUID, searchInput } = action.payload;
        const existingData = state.inputToQuantityMap[clientSearchInputUUID];
        const nextSearchInputUUID = searchInput.uuid;

        if (!existingData) {
          return state;
        }

        const nextInputToQuantityMap = {
          ...state.inputToQuantityMap,
        };
        delete nextInputToQuantityMap[clientSearchInputUUID];
        nextInputToQuantityMap[nextSearchInputUUID] = {
          quantity: existingData.quantity ?? searchInput.quantity,
          variantPackUUID:
            existingData.variantPackUUID ??
            searchInput.search_term.variants_pack?.uuid ??
            '',
        };

        const nextListItems = state.listItems.map<InboundReducerItem>(
          (listItem) => {
            if (
              listItem.metadata?.inputWithResult.uuid !== clientSearchInputUUID
            ) {
              return listItem;
            }

            const nextInputWithResult = buildSearchInput(searchInput);
            return {
              ...listItem,
              metadata: {
                ...listItem.metadata!,
                inputWithResult: nextInputWithResult,
                variantPack:
                  searchInput.search_term.variants_pack ??
                  listItem.metadata?.variantPack ??
                  null,
              },
              value: nextSearchInputUUID,
            };
          },
        );

        const nextMatchTerms = state.matchTerms.map((term) => {
          if (term.searchInputUUID === clientSearchInputUUID) {
            return {
              ...term,
              searchInputUUID: nextSearchInputUUID,
            };
          }
          return term;
        });

        return {
          ...state,
          inputToQuantityMap: nextInputToQuantityMap,
          listItems: nextListItems,
          matchTerms: nextMatchTerms,
        };
      }
      case 'SWAP_ITEM': {
        const { itemMetadata, previousItem, searchInput } = action.payload;
        const nextItemMetadata = removeAdComponents(itemMetadata);
        let nextSearchInputUUID = previousItem.metadata!.inputWithResult.uuid;
        const nextListItems = state.listItems.map<InboundReducerItem>(
          (listItem) => {
            if (listItem.value === previousItem.value) {
              const currentResult = previousItem.metadata!.inputWithResult;
              const nextInputWithResult: SearchInput = searchInput
                ? {
                    ...currentResult,
                    result: searchInput,
                    status: searchInput.search_term
                      .status as SmartOrderSearchTermStatus,
                  }
                : currentResult;
              const nextVariantPack = searchInput
                ? searchInput.search_term.variants_pack
                : null;
              const selectedListItem: InboundReducerItem = {
                ...previousItem,
                metadata: {
                  inputWithResult: nextInputWithResult,
                  listOption: {
                    metadata: {
                      ...nextItemMetadata,
                      skus: [nextItemMetadata],
                    },
                    title: nextItemMetadata.displayName,
                    value: nextItemMetadata.variantPackUUID,
                  },
                  matchTerm: previousItem.metadata!.matchTerm,
                  variantPack: nextVariantPack || null,
                },
              };
              return selectedListItem;
            }
            return listItem;
          },
        );

        // Set quantity of newly selected item to the previous item's quantity
        // or the search input quantity if there's no previously selected / matched
        // variant pack
        const previousSearchInput = previousItem.metadata!.inputWithResult;
        let previousItemQuantity =
          state.inputToQuantityMap[previousSearchInput.uuid]?.quantity ??
          previousSearchInput.quantity;
        const nextItemQuantity =
          searchInput?.quantity ?? previousItemQuantity ?? 0;

        return {
          ...state,
          inputToQuantityMap: {
            ...state.inputToQuantityMap,
            [nextSearchInputUUID]: {
              quantity: nextItemQuantity,
              variantPackUUID: itemMetadata.variantPackUUID,
            },
          },
          listItems: nextListItems,
        };
      }
      case 'SET_ITEM_STATUS': {
        const { itemUUID, status } = action.payload;
        const nextListItems = state.listItems.map<InboundReducerItem>(
          (listItem) => {
            if (listItem.value === itemUUID) {
              const currentResult = listItem.metadata!.inputWithResult;

              return {
                ...listItem,
                metadata: {
                  ...listItem.metadata!,
                  inputWithResult: {
                    ...currentResult,
                    status,
                  },
                },
              };
            }
            return listItem;
          },
        );

        return {
          ...state,
          listItems: nextListItems,
        };
      }
      case 'UPDATE_ITEM_NOTES': {
        const { itemNotes, searchInputUUID } = action.payload;
        return {
          ...state,
          listItems: state.listItems.map((listItem) =>
            listItem.metadata?.inputWithResult.uuid === searchInputUUID
              ? {
                  ...listItem,
                  metadata: {
                    ...listItem.metadata,
                    inputWithResult: {
                      ...listItem.metadata.inputWithResult,
                      itemNotes,
                    },
                  },
                }
              : listItem,
          ),
        };
      }
      case 'UPDATE_QUANTITY': {
        const { quantity, searchInputUUID } = action.payload;
        const existingData = state.inputToQuantityMap[searchInputUUID];
        if (!existingData) {
          return state;
        }
        return {
          ...state,
          inputToQuantityMap: {
            ...state.inputToQuantityMap,
            [searchInputUUID]: {
              ...existingData,
              quantity,
            },
          },
        };
      }
      case 'REMOVE_ITEMS': {
        const { searchInputUUIDs } = action.payload;
        const nextListItems = state.listItems.filter((listItem) => {
          const inputListItemUUID = listItem.metadata?.inputWithResult.uuid;
          return (
            !inputListItemUUID || !searchInputUUIDs.includes(inputListItemUUID)
          );
        });

        const nextInputToQuantityMap = {
          ...state.inputToQuantityMap,
        };

        for (const searchInputUUID of searchInputUUIDs) {
          delete nextInputToQuantityMap[searchInputUUID];
        }

        return {
          ...state,
          inputToQuantityMap: nextInputToQuantityMap,
          listItems: nextListItems,
          matchTerms: state.matchTerms.filter((term) => {
            const inputListItemUUID = term.searchInputUUID;
            return !searchInputUUIDs.includes(inputListItemUUID);
          }),
        };
      }
      default: {
        return endReducer(state, action);
      }
    }
  };
};

export const useOrderInboundReducer = () => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const { getVariantPackInfo } = useVariantPackInfoContext();

  return useReducer(
    orderInboundReducer(adaptAndGroupVariantPacks, getVariantPackInfo),
    {
      inputToQuantityMap: {},
      listItems: [],
      matchTerms: [],
      quantitiesAdjusted: false,
    },
  );
};
